/**
 * Order a gameplan Line's nodes into a walkable sequence for guided
 * playback (the path tutorial).
 *
 * A Line is a directed graph, usually a clean chain but occasionally
 * branchy or with loop-back edges ("they defend → reset"). We try to
 * produce a single linear walk:
 *
 *   1. Find the entry — the node with no incoming (forward) edge.
 *   2. Walk outgoing edges, taking the first at each branch, until we
 *      revisit a node or run out.
 *
 * If that walk is clean (single entry, covers every node, no revisit),
 * we use it. Otherwise we fall back to the stored `nodes[]` order — the
 * order the user added them, which is a reasonable read even for a
 * branchy line. Playback never crashes on a messy line; it just walks
 * the array.
 */
import type { Line, LineNode } from '../types/gameplan';

export function orderLineNodes(line: Line): LineNode[] {
  const nodes = line.nodes;
  if (nodes.length <= 1) return nodes;

  const byId = new Map(nodes.map((n) => [n.id, n]));
  const outgoing = new Map<string, string[]>();
  const indeg = new Map<string, number>();
  for (const n of nodes) {
    outgoing.set(n.id, []);
    indeg.set(n.id, 0);
  }
  for (const e of line.edges) {
    if (!byId.has(e.from) || !byId.has(e.to)) continue; // dangling
    outgoing.get(e.from)!.push(e.to);
    indeg.set(e.to, (indeg.get(e.to) ?? 0) + 1);
  }

  // Entry = the (single) node with in-degree 0.
  const entries = nodes.filter((n) => (indeg.get(n.id) ?? 0) === 0);
  if (entries.length !== 1) return nodes; // 0 = pure cycle, >1 = forest

  const walk: LineNode[] = [];
  const seen = new Set<string>();
  let cur: string | undefined = entries[0].id;
  while (cur && !seen.has(cur)) {
    seen.add(cur);
    const node = byId.get(cur);
    if (node) walk.push(node);
    cur = outgoing.get(cur)?.[0]; // first branch
  }

  // Clean chain only if the walk reached every node.
  return walk.length === nodes.length ? walk : nodes;
}
